import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell, LabelList } from 'recharts';
import { Card, LoadingSpinner } from '../ui';
import { useTheme } from '../../contexts/ThemeContext';

export default function MaxDiffChart({ data, title, label, loading, error, height = 360 }) {
  const { theme } = useTheme();
  const [view, setView] = useState('net');

  const rows = (Array.isArray(data) ? data : []).map(item => ({
    option: item.option,
    net: item.net ?? 0,
    best: item.best ?? 0,
    worst: item.worst ?? 0,
    bestPct: item.bestPct ?? 0,
    worstPct: item.worstPct ?? 0,
  }));

  const netData = rows;
  const splitData = rows.map(r => ({
    ...r,
    worstNeg: -(r.worstPct ?? 0),
    bestPos: r.bestPct ?? 0,
  }));

  // const concernColor = '#ef4444';
  // const reassureColor = '#64cc9c';
  const concernColor = '#8B5CF6';
  const reassureColor = '#684bab';
  const xAxisTickColor = theme === 'dark' ? '#9a9a9a' : '#838383';
  const yAxisTickColor = theme === 'dark' ? '#dbdbdb' : '#2a2a2a';
  const gridColor = theme === 'dark' ? '#374151' : '#e5e7eb';
  const zeroLineColor = theme === 'dark' ? '#a2a2a2' : '#9ca3af';

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div className="bg-background-light dark:bg-background-gray border border-border-light dark:border-border rounded-lg p-2 text-xs">
          <p className="font-semibold text-text-primary-light dark:text-text-primary mb-1">{d.option}</p>
          <p className="text-text-secondary-light dark:text-text-secondary">Most concerning: {d.best} ({d.bestPct}%)</p>
          <p className="text-text-secondary-light dark:text-text-secondary">Least concerning: {d.worst} ({d.worstPct}%)</p>
          <p className="text-text-primary-light dark:text-text-primary mt-1 font-medium">Net: {d.net > 0 ? '+' : ''}{d.net}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        {(title || label) && (
          <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary">
            {title || label}
          </h3>
        )}
        <div className="flex items-center gap-1 border border-border-light dark:border-border rounded-lg p-0.5 bg-surface dark:bg-surface-dark">
          <button
            onClick={() => setView('net')}
            className={`px-2.5 py-1 text-xs font-medium rounded transition-colors cursor-pointer ${view === 'net' ? 'bg-brand-primary text-text-primary-light' : 'text-text-secondary-light dark:text-text-secondary hover:opacity-75'}`}
          >
            Net
          </button>
          <button
            onClick={() => setView('split')}
            className={`px-2.5 py-1 text-xs font-medium rounded transition-colors cursor-pointer ${view === 'split' ? 'bg-brand-primary text-text-primary-light' : 'text-text-secondary-light dark:text-text-secondary hover:opacity-75'}`}
          >
            Split
          </button>
        </div>
      </div>
      <div style={{ height: `${height}px` }} className="w-full">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-danger-dark dark:text-danger">
            <p>{error}</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex items-center justify-center h-full text-text-muted-light dark:text-text-muted">
            <p>No data available</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={view === 'net' ? netData : splitData}
              layout="vertical"
              margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
              stackOffset={view === 'split' ? 'sign' : undefined}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis
                type="number"
                domain={[-100, 100]}
                tickFormatter={(v) => `${Math.abs(v)}%`}
                tick={{ fill: xAxisTickColor, fontSize: 11 }}
              />
              <YAxis
                type="category"
                dataKey="option"
                width={180}
                tick={{ fill: yAxisTickColor, fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }} />
              <ReferenceLine x={0} stroke={zeroLineColor} />
              {view === 'net' ? (
                <Bar dataKey="net" radius={[0, 4, 4, 0]}>
                  {netData.map((entry, i) => (
                    <Cell key={i} fill={entry.net >= 0 ? concernColor : reassureColor} />
                  ))}
                  <LabelList
                    dataKey="net"
                    position="right"
                    formatter={(v) => `${v > 0 ? '+' : ''}${v}%`}
                    style={{ fill: yAxisTickColor, fontSize: 11, fontWeight: 500 }}
                  />
                </Bar>
              ) : (
                <>
                  <Bar dataKey="worstNeg" stackId="a" fill={reassureColor} radius={[0, 4, 4, 0]}>
                    <LabelList
                      dataKey="worst"
                      position="right"
                      style={{ fill: yAxisTickColor, fontSize: 11, fontWeight: 500 }}
                    />
                  </Bar>
                  <Bar dataKey="bestPos" stackId="a" fill={concernColor} radius={[0, 4, 4, 0]}>
                    <LabelList
                      dataKey="best"
                      position="right"
                      style={{ fill: yAxisTickColor, fontSize: 11, fontWeight: 500 }}
                    />
                  </Bar>
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}
